import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import {
  EsOntologyField,
  useCreateEsField,
  useGetEsOntologyFields,
  useUpdateEsField,
} from "@/service-hooks/use-get-es-ontology-service";

interface EsFieldDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectUuid: string;
  dataLakeId: string;
  indexId: string;
  indexName?: string;
  existing?: EsOntologyField;
  onSaved?: () => void;
}

interface FormState {
  name: string;
  fullPath: string;
  parentPath: string;
  type: string;
  analyzer: string;
  searchAnalyzer: string;
  format: string;
  copyTo: string;
  index: boolean;
  docValues: boolean;
  isNested: boolean;
  isObject: boolean;
  isMultiField: boolean;
  ignoreAbove: string;
}

const EMPTY: FormState = {
  name: "",
  fullPath: "",
  parentPath: "",
  type: "keyword",
  analyzer: "",
  searchAnalyzer: "",
  format: "",
  copyTo: "",
  index: true,
  docValues: true,
  isNested: false,
  isObject: false,
  isMultiField: false,
  ignoreAbove: "",
};

export default function EsFieldDialog({
  isOpen,
  onClose,
  projectUuid,
  dataLakeId,
  indexId,
  indexName,
  existing,
  onSaved,
}: EsFieldDialogProps) {
  const [form, setForm] = useState<FormState>({ ...EMPTY });
  // True once the user has typed in the fullPath input directly. When still
  // false (the default), fullPath auto-tracks `${parentPath ? parentPath + "." : ""}${name}`.
  const [fullPathTouched, setFullPathTouched] = useState(false);
  const createField = useCreateEsField();
  const updateField = useUpdateEsField();
  const isEdit = !!existing?.id;
  const isPending = createField.isPending || updateField.isPending;

  // Sibling fields under the same index — used to power the parent-path
  // suggestion list. Only fields that can be parents (object/nested) are
  // valid as a `parentPath`, but we surface all of them and let ES validate
  // on ingest if the user picks something unusual.
  const fieldsQuery = useGetEsOntologyFields(dataLakeId, isOpen && !isEdit, { projectUuid, indexId });
  const parentPathOptions = useMemo(() => {
    const items = fieldsQuery.data ?? [];
    return items
      .map((f) => f.fullPath)
      .filter((p): p is string => !!p)
      .sort();
  }, [fieldsQuery.data]);

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      name: existing?.name ?? "",
      fullPath: existing?.fullPath ?? "",
      parentPath: existing?.parentPath ?? "",
      type: existing?.type ?? "keyword",
      analyzer: existing?.analyzer ?? "",
      searchAnalyzer: existing?.searchAnalyzer ?? "",
      format: existing?.format ?? "",
      copyTo: (existing?.copyTo ?? []).join(", "),
      index: existing?.index !== false,
      docValues: existing?.docValues !== false,
      isNested: !!existing?.isNested,
      isObject: !!existing?.isObject,
      isMultiField: !!existing?.isMultiField,
      ignoreAbove:
        typeof existing?.ignoreAbove === "number" ? String(existing.ignoreAbove) : "",
    });
    // Edits start with an already-populated fullPath that should NOT auto-track
    // subsequent name/parentPath edits (the field is disabled anyway in edit mode).
    setFullPathTouched(!!existing?.fullPath);
  }, [isOpen, existing]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => {
      // While the user hasn't manually edited fullPath, keep it in sync with
      // `${parentPath ? parentPath + "." : ""}${name}` on each name/parentPath
      // edit. This is more predictable than the previous useEffect that only
      // ran when fullPath happened to be empty.
      if (!fullPathTouched && !isEdit && (key === "name" || key === "parentPath")) {
        const nextName = key === "name" ? (value as string) : prev.name;
        const nextParent = key === "parentPath" ? (value as string) : prev.parentPath;
        const auto = nextParent ? `${nextParent}.${nextName}` : nextName;
        return { ...prev, [key]: value, fullPath: auto };
      }
      return { ...prev, [key]: value };
    });
  };

  const updateFullPath = (value: string) => {
    setFullPathTouched(true);
    setForm((prev) => ({ ...prev, fullPath: value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.fullPath.trim() || !form.type.trim()) return;
    const copyToArr = form.copyTo
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const ignoreAboveNum = form.ignoreAbove.trim()
      ? Number(form.ignoreAbove.trim())
      : undefined;
    try {
      if (isEdit && existing?.id) {
        await updateField.mutateAsync({
          id: existing.id,
          projectUuid,
          dataLakeId,
          data: {
            type: form.type.trim(),
            analyzer: form.analyzer.trim() || undefined,
            searchAnalyzer: form.searchAnalyzer.trim() || undefined,
            format: form.format.trim() || undefined,
            copyTo: copyToArr.length > 0 ? copyToArr : undefined,
            index: form.index,
            docValues: form.docValues,
            ignoreAbove:
              ignoreAboveNum !== undefined && Number.isFinite(ignoreAboveNum)
                ? ignoreAboveNum
                : undefined,
          },
        });
      } else {
        await createField.mutateAsync({
          indexId,
          projectUuid,
          dataLakeId,
          data: {
            name: form.name.trim(),
            fullPath: form.fullPath.trim(),
            parentPath: form.parentPath.trim() || undefined,
            type: form.type.trim(),
            analyzer: form.analyzer.trim() || undefined,
            searchAnalyzer: form.searchAnalyzer.trim() || undefined,
            format: form.format.trim() || undefined,
            copyTo: copyToArr.length > 0 ? copyToArr : undefined,
            index: form.index,
            docValues: form.docValues,
            isNested: form.isNested,
            isObject: form.isObject,
            isMultiField: form.isMultiField,
            ignoreAbove:
              ignoreAboveNum !== undefined && Number.isFinite(ignoreAboveNum)
                ? ignoreAboveNum
                : undefined,
          },
        });
      }
      onSaved?.();
      onClose();
    } catch {
      // toast handled in mutation
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit ES Field" : "Add ES Field"}
            {indexName ? ` — ${indexName}` : ""}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Configure the field fields for the selected ES index.
          </DialogDescription>
        </DialogHeader>

        <div className={`space-y-4 overflow-y-auto flex-1 pr-1 ${CUSTOM_SCROLLBAR}`}>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">
                Name <span className="text-error">*</span>
              </label>
              <Input
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="e.g. firstName"
                className="bg-surface text-white"
                disabled={isEdit}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">
                Type <span className="text-error">*</span>
              </label>
              <Input
                value={form.type}
                onChange={(e) => update("type", e.target.value)}
                placeholder="keyword, long, date, nested, object, ..."
                className="bg-surface text-white"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">Parent Path</label>
            <Input
              value={form.parentPath}
              onChange={(e) => update("parentPath", e.target.value)}
              placeholder="Leave empty for root-level fields"
              className="bg-surface text-white"
              list="es-field-parent-paths"
              disabled={isEdit}
            />
            {!isEdit && parentPathOptions.length > 0 && (
              <datalist id="es-field-parent-paths">
                {parentPathOptions.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">
              Full Path <span className="text-error">*</span>
            </label>
            <Input
              value={form.fullPath}
              onChange={(e) => updateFullPath(e.target.value)}
              placeholder="e.g. contacts.localizations.firstName"
              className="bg-surface text-white font-mono text-xs"
              disabled={isEdit}
            />
            {!isEdit && (
              <p className="text-xs text-muted">
                Auto-derived from parent path + name unless edited.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">Analyzer</label>
              <Input
                value={form.analyzer}
                onChange={(e) => update("analyzer", e.target.value)}
                placeholder="standard"
                className="bg-surface text-white"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">Search Analyzer</label>
              <Input
                value={form.searchAnalyzer}
                onChange={(e) => update("searchAnalyzer", e.target.value)}
                placeholder=""
                className="bg-surface text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">Format</label>
              <Input
                value={form.format}
                onChange={(e) => update("format", e.target.value)}
                placeholder="strict_date_optional_time"
                className="bg-surface text-white"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">Ignore Above</label>
              <Input
                type="number"
                value={form.ignoreAbove}
                onChange={(e) => update("ignoreAbove", e.target.value)}
                placeholder="256"
                className="bg-surface text-white"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">Copy To</label>
            <Input
              value={form.copyTo}
              onChange={(e) => update("copyTo", e.target.value)}
              placeholder="Comma-separated target field paths"
              className="bg-surface text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm text-white">
              <Switch
                checked={form.index}
                onCheckedChange={(v) => update("index", !!v)}
              />
              Indexed
            </label>
            <label className="flex items-center gap-2 text-sm text-white">
              <Switch
                checked={form.docValues}
                onCheckedChange={(v) => update("docValues", !!v)}
              />
              Doc Values
            </label>
          </div>

          {!isEdit && (
            <div className="grid grid-cols-3 gap-3">
              <label className="flex items-center gap-2 text-sm text-white">
                <Switch
                  checked={form.isNested}
                  onCheckedChange={(v) => update("isNested", !!v)}
                />
                Nested
              </label>
              <label className="flex items-center gap-2 text-sm text-white">
                <Switch
                  checked={form.isObject}
                  onCheckedChange={(v) => update("isObject", !!v)}
                />
                Object
              </label>
              <label className="flex items-center gap-2 text-sm text-white">
                <Switch
                  checked={form.isMultiField}
                  onCheckedChange={(v) => update("isMultiField", !!v)}
                />
                Multi-field
              </label>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-surface-border">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              !form.name.trim() ||
              !form.fullPath.trim() ||
              !form.type.trim() ||
              isPending
            }
            className="bg-primary hover:bg-primary-dark text-white"
          >
            {isPending ? (isEdit ? "Saving..." : "Adding...") : isEdit ? "Save" : "Add"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
